import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { promosService, PromoCode, PromoDetail } from '../../../services/promos.service'
import LoadingSpinner from '../../../components/LoadingSpinner'

/**
 * Admin promo codes: create/edit/deactivate codes that set the wallet
 * activation fee, with per-code usage counts and an audit trail.
 */

interface PromoFormData {
  code: string
  amountMYR: string
  isActive: boolean
}

const emptyForm = (): PromoFormData => ({ code: '', amountMYR: '80', isActive: true })

function StatusPill({ active, onToggle, disabled, label }: { active: boolean; onToggle?: () => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      title={label}
      className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors disabled:opacity-50 ${
        active ? 'bg-[#0D7330]' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          active ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

export default function PromoCodesPage() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [codes, setCodes] = useState<PromoCode[]>([])
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Create / edit form
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<PromoFormData>(emptyForm())
  const [saving, setSaving] = useState(false)
  const [nearDuplicate, setNearDuplicate] = useState<string | null>(null)

  // Detail (edit mode): audit trail + redemptions
  const [detail, setDetail] = useState<PromoDetail | null>(null)

  const load = async () => {
    try {
      setCodes(await promosService.list())
    } catch (err: any) {
      setError(err.response?.data?.message || t('promos.loadFailed', 'Failed to load promo codes'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openCreate = () => {
    setEditingId(null)
    setDetail(null)
    setForm(emptyForm())
    setNearDuplicate(null)
    setError(null)
    setFormOpen(true)
  }

  const openEdit = async (code: PromoCode) => {
    setEditingId(code.id)
    setForm({ code: code.code, amountMYR: String(code.amountMYR), isActive: code.isActive })
    setNearDuplicate(null)
    setError(null)
    setFormOpen(true)
    try {
      setDetail(await promosService.detail(code.id))
    } catch {
      setDetail(null)
    }
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditingId(null)
    setDetail(null)
    setNearDuplicate(null)
  }

  const handleSave = async (confirmCaseDuplicate = false) => {
    const amount = parseFloat(form.amountMYR)
    if (!form.code.trim() || !/^[A-Za-z0-9]{1,10}$/.test(form.code.trim())) {
      toast.error(t('promos.invalidCodeFormat', 'Code must be 1-10 alphanumeric characters'))
      return
    }
    if (!Number.isFinite(amount) || amount <= 0 || amount > 100) {
      toast.error(t('promos.invalidAmount', 'Amount must be between RM0.01 and RM100'))
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (editingId) {
        await promosService.update(editingId, {
          amountMYR: amount,
          isActive: form.isActive,
        })
        toast.success(t('promos.created', 'Promo code created'))
      } else {
        await promosService.create({
          code: form.code.trim(),
          amountMYR: amount,
          isActive: form.isActive,
          confirmCaseDuplicate,
        })
        toast.success(t('promos.updated', 'Promo code updated'))
      }
      closeForm()
      await load()
    } catch (err: any) {
      const data = err.response?.data
      if (data?.needsConfirmation && data?.similarCode) {
        setNearDuplicate(data.similarCode)
        setError(
          t('promos.nearDuplicateWarning', {
            defaultValue: 'A similar code "{{similarCode}}" already exists (codes are case-sensitive). Create it anyway?',
            similarCode: data.similarCode,
          }),
        )
      } else {
        setError(data?.message || err.message || t('promos.saveFailed', 'Failed to save the promo code'))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (code: PromoCode) => {
    try {
      await promosService.update(code.id, { isActive: !code.isActive })
      toast.success(
        code.isActive
          ? t('promos.deactivatedToast', 'Code deactivated')
          : t('promos.activatedToast', 'Code activated'),
      )
      await load()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('promos.toggleFailed', 'Failed to update the code status'))
    }
  }

  const filtered = codes.filter((c) =>
    search.trim() ? c.code.toLowerCase().includes(search.trim().toLowerCase()) : true,
  )

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('promos.title', 'Promo Codes')}</h1>
            <p className="text-gray-600 mt-1 text-sm">
              {t('promos.subtitle', 'Set the promotional wallet activation fee')}
            </p>
          </div>
          {!formOpen && (
            <button
              onClick={openCreate}
              className="shrink-0 rounded-md bg-[#0D7330] px-4 py-2 text-sm font-medium text-white hover:bg-[#0a5c26]"
            >
              {t('promos.newCode', '+ New Code')}
            </button>
          )}
        </div>

        {/* Create / Edit form */}
        {formOpen && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingId ? t('promos.formTitleEdit', 'Edit Promo Code') : t('promos.formTitleCreate', 'New Promo Code')}
            </h2>

            {nearDuplicate && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
                {t('promos.nearDuplicateWarning', {
                  defaultValue:
                    'A similar code "{{similarCode}}" already exists (codes are case-sensitive). Create it anyway?',
                  similarCode: nearDuplicate,
                })}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    className="px-3 py-1.5 bg-amber-600 text-white rounded-md text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
                  >
                    {t('promos.confirmCreate', 'Yes, create this code')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNearDuplicate(null)}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium"
                  >
                    {t('promos.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('promos.codeLabel', 'Code')} *
                </label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="WELCOME80"
                  maxLength={10}
                  disabled={!!editingId}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-[#0D7330] focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {editingId
                    ? t('promos.codeHelperEdit', 'The code cannot be changed after creation.')
                    : t('promos.codeHelperCreate', '1-10 alphanumeric characters. Case-sensitive.')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('promos.amountLabel', 'Activation Fee (RM)')} *
                </label>
                <input
                  type="number"
                  value={form.amountMYR}
                  onChange={(e) => setForm({ ...form, amountMYR: e.target.value })}
                  min="0.01"
                  max="100"
                  step="0.01"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('promos.amountHelper', 'Final fee (not a discount). Maximum RM100.')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('promos.statusLabel', 'Status')}
                </label>
                <div className="flex items-center gap-3 h-[42px]">
                  <StatusPill
                    active={form.isActive}
                    onToggle={() => setForm({ ...form, isActive: !form.isActive })}
                    label={form.isActive ? t('promos.active', 'Active') : t('promos.inactive', 'Inactive')}
                  />
                  <span className={`text-sm ${form.isActive ? 'text-[#0D7330]' : 'text-gray-500'}`}>
                    {form.isActive ? t('promos.active', 'Active') : t('promos.inactive', 'Inactive')}
                  </span>
                </div>
              </div>
            </div>

            {/* Audit trail + redemptions (edit mode only) */}
            {editingId && detail && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-200 pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('promos.auditTitle', 'Audit Trail')}
                  </h3>
                  {detail.audits.length === 0 ? (
                    <p className="text-xs text-gray-500">{t('promos.noAudits', 'No changes recorded.')}</p>
                  ) : (
                    <ul className="space-y-2">
                      {detail.audits.map((a) => (
                        <li key={a.id} className="text-xs text-gray-600 border-l-2 border-gray-200 pl-3">
                          <span className="font-medium">{a.action}</span> {t('promos.by', 'by')}{' '}
                          {a.actorEmail ?? '—'} · {new Date(a.createdAt).toLocaleString()}
                          <div className="text-gray-500 mt-0.5">
                            {Object.entries(a.changes).map(([field, change]) => {
                              const c = change as { from?: unknown; to?: unknown }
                              return (
                                <div key={field}>
                                  {field}: {String(c.from ?? '—')} → {String(c.to ?? '—')}
                                </div>
                              )
                            })}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('promos.usageTitle', 'Usage ({{count}})', { count: detail.usageCount })}
                  </h3>
                  {detail.redemptions.length === 0 ? (
                    <p className="text-xs text-gray-500">{t('promos.noRedemptions', 'Not used yet.')}</p>
                  ) : (
                    <ul className="space-y-1">
                      {detail.redemptions.map((r) => (
                        <li key={r.id} className="text-xs text-gray-600 flex justify-between">
                          <span>{r.email || r.memberId || '—'}</span>
                          <span className="font-mono">RM{Number(r.feeAppliedMYR).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => handleSave(false)}
                disabled={saving || !!nearDuplicate}
                className="px-5 py-2 bg-[#0D7330] text-white font-medium rounded-lg hover:bg-[#0a5c26] disabled:opacity-50"
              >
                {saving ? t('promos.saving', 'Saving...') : t('promos.save', 'Save')}
              </button>
              <button onClick={closeForm} className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700">
                {t('promos.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('promos.searchPlaceholder', 'Search codes...')}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">{t('promos.emptyList', 'No promo codes yet.')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('promos.codeColumn', 'Code')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('promos.feeColumn', 'Fee (RM)')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('promos.statusColumn', 'Status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('promos.usageColumn', 'Usage')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('promos.createdColumn', 'Created')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t('promos.actionsColumn', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 font-mono font-medium text-gray-900">{c.code}</td>
                    <td className="px-4 py-3 text-gray-700">RM{Number(c.amountMYR).toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <StatusPill
                        active={c.isActive}
                        onToggle={() => handleToggle(c)}
                        label={c.isActive ? t('promos.active', 'Active') : t('promos.inactive', 'Inactive')}
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-700">{c.usageCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(c.createdAt).toLocaleDateString()}
                      {c.createdByEmail && <div className="text-gray-400">{c.createdByEmail}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(c)} className="text-sm text-[#0D7330] hover:underline">
                        {t('promos.edit', 'Edit')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
